(ns frontend.view.settings2.permissions.field-settings.table
  (:require
   [clojure.set :as set]
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.fields.table-column-widths :as widths]
   [common.localization :as loc]
   [common.permissions.field-permissions :as field-permissions]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.registry :as registry]
   [frontend.db :as app-db]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.react.virtualized.scrollable-grid.view :as scrollable-grid]
   [frontend.react.virtualized.utils :as vu]
   [frontend.state.current-session :as session]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.view.modals.copy-permissions.view :as copy-permissions]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.options-management.view :as options-management]
   [frontend.view.settings2.permissions.field-settings.db :as db]
   [frontend.view.settings2.permissions.field-settings.events :as events]
   [reagent.core :as r]))

(defn in-use-setting
  [{:keys [entity-type column-attribute field-attribute value settings-state disabled?]}]
  [:label
   {:class "relative flex cursor-pointer items-center m-1 rounded-md"}
   [:input
    (merge
     {:class     "peer cursor-pointer appearance-none relative h-5 w-5 bg-transparent disabled:bg-disabled-light border border-input-light-border disabled:border-disabled-light-border transition-all checked:border-input-gloomy-border checked:bg-input-gloomy rounded disabled:cursor-default"
      :type      "checkbox"
      :checked   (boolean (some #{value} field-permissions/POSITIVE-IN-USE-VALUES))
      :on-change (fn [e]
                   (let [checked? (->> e
                                       .-target
                                       .-checked
                                       boolean)
                         value (if checked? :field-in-use :field-not-in-use)]
                     (swap! settings-state assoc-in
                            [entity-type field-attribute column-attribute]
                            value)
                     ;; We MUST Deref the atom to propagate changes to the root node - reagent documentation is lacking - cursors are LAZY
                     (= (get-in @settings-state
                                [entity-type field-attribute column-attribute])
                        value)))}
     (when disabled?
       {:disabled true}))]
   [:span
    {:class "flex items-center justify-center pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-input-gloomy-text opacity-0 transition-opacity peer-checked:opacity-100"}
    [:svg {:class        "h-3.5 w-3.5"
           :xmlns        "http://www.w3.org/2000/svg"
           :fill         "currentColor"
           :viewBox      "0 0 20 20"
           :stroke       "currentColor"
           :stroke-width "1"}
     [:path {:fill-rule "evenodd"
             :clip-rule "evenodd"
             :d         "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"}]]]])

(defn field-options-setting
  [{:keys [field-schema field-attribute entity-type]}]
  (let [named-value? (-> field-schema
                         :named-value-attribute)
        pipeline-url (pipeline/pipeline-url entity-type)
        pipeline-attribute (pipeline/get-pipeline-key-by-url pipeline-url)
        selected-field field-attribute]
    (when named-value?
      [:span
       [:a
        {:class    "underline text-base-light-text-accent cursor-pointer"
         :on-click (fn []
                     (modals/open-modal! options-management/modal-key
                                         {:pipeline-attribute pipeline-attribute
                                          :selected-field     selected-field}))}
        (loc/tr :manage-options)]])))

(defn team-setting
  [{:keys [entity-type column-attribute field-attribute value settings-state disabled?]}]
  (let [tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema field-attribute)]
    (into [:select
           {:class     "w-44 m-0 p-1 text-sm text-base-light-text outline-0 border-base-light-border-paler bg-base-light disabled:bg-disabled-light disabled:text-disabled-light-text"
            :value     (if (keyword? value)
                         (name value)
                         "")
            :disabled  (and (boolean disabled?)
                            (not (tenant-customizations/tesi?))
                            (or (= :role/system (get-in field-schema [:field/default-authorization :access/write]))
                                (= :role/system (get-in field-schema [:field/default-authorization :access/read]))))
            :on-change (fn [e]
                         (let [v (->> e
                                      .-target
                                      .-value)
                               kw-value (keyword v)]
                           (swap! settings-state assoc-in
                                  [entity-type field-attribute column-attribute]
                                  kw-value)
                           ;; We MUST Deref the atom to propagate changes to the root node - reagent documentation is lacking - cursors are LAZY
                           (= (get-in @settings-state [entity-type field-attribute column-attribute])
                              kw-value)))}]
          (concat []
                  (when-not (and (boolean disabled?)
                                 (= :role/system
                                    (get-in field-schema [:field/default-authorization
                                                          :access/write])))
                    [[:option
                      {:value "none"} "None"]])
                  [[:option
                    (merge {:value "r"}
                           (when-not (session/has-feature-access? :feature/read-only-access)
                             {:disabled true}))
                    "Read"]]
                  (when-not (= :role/system (get-in field-schema [:field/default-authorization :access/write]))
                    [[:option
                      {:value "w"} "Write"]
                     (when (->> field-attribute
                                (registry/get-field-schema tenant-schema)
                                :create-on-fly?)
                       [:option
                        {:value "x"} "Write and manage options"])])))))

(defn selected-user-setting
  [_field-attribute value]
  [:span
   (case value
     :none "None"
     :r "Read"
     :w "Write"
     :x "Write and manage options"
     :not-in-use "-"
     nil "Not in any team"
     (str "Unknown " value))])

(defn zadmin-setting
  [field-attribute value zadmin-state]
  (let [tenant-schema @session/tenant-schema]
    [:select
     {:class "w-44 m-0 p-1 text-sm text-base-light-text border-base-light-border-paler bg-base-light disabled:bg-disabled-light disabled:text-disabled-light-text"
      :value (if (keyword? value)
               (name value)
               "")
      :on-change
      (fn [e]
        (let [v (keyword (->> e
                              .-target
                              .-value))]
          (case v
            :whitelist
            (swap! zadmin-state assoc
                   :tenant/whitelisted-custom-fields
                   (set/union (set (some->> zadmin-state
                                            (deref)
                                            :tenant/whitelisted-custom-fields))
                              #{field-attribute}
                              (when-let [secondary-attribute (->> field-attribute
                                                                  (registry/get-field-schema
                                                                   tenant-schema)
                                                                  :field/secondary-attribute)]
                                #{secondary-attribute}))
                   :tenant/blacklisted-fields
                   (set/difference (set (some->> zadmin-state
                                                 (deref)
                                                 :tenant/blacklisted-fields))
                                   #{field-attribute}
                                   (when-let [secondary-attribute (->> field-attribute
                                                                       (registry/get-field-schema
                                                                        tenant-schema)
                                                                       :field/secondary-attribute)]
                                     #{secondary-attribute})))
            :blacklist
            (swap! zadmin-state assoc
                   :tenant/whitelisted-custom-fields
                   (set/difference (set (some->> zadmin-state
                                                 (deref)
                                                 :tenant/whitelisted-custom-fields))
                                   #{field-attribute}
                                   (when-let [secondary-attribute (->> field-attribute
                                                                       (registry/get-field-schema
                                                                        tenant-schema)
                                                                       :field/secondary-attribute)]
                                     #{secondary-attribute}))
                   :tenant/blacklisted-fields
                   (set/union (set (some->> zadmin-state
                                            (deref)
                                            :tenant/blacklisted-fields))
                              #{field-attribute}
                              (when-let [secondary-attribute (->> field-attribute
                                                                  (registry/get-field-schema
                                                                   tenant-schema)
                                                                  :field/secondary-attribute)]
                                #{secondary-attribute})))
            (swap! zadmin-state assoc
                   :tenant/whitelisted-custom-fields
                   (set/difference (set (some->> zadmin-state
                                                 (deref)
                                                 :tenant/whitelisted-custom-fields))
                                   #{field-attribute}
                                   (when-let [secondary-attribute (->> field-attribute
                                                                       (registry/get-field-schema
                                                                        tenant-schema)
                                                                       :field/secondary-attribute)]
                                     #{secondary-attribute}))
                   :tenant/blacklisted-fields
                   (set/difference (set (some->> zadmin-state
                                                 (deref)
                                                 :tenant/blacklisted-fields))
                                   #{field-attribute}
                                   (when-let [secondary-attribute (->> field-attribute
                                                                       (registry/get-field-schema
                                                                        tenant-schema)
                                                                       :field/secondary-attribute)]
                                     #{secondary-attribute}))
                   :tenant/cleared-zadmin-conf-fields
                   (set/union (set (some->> zadmin-state
                                            (deref)
                                            :tenant/cleared-zadmin-conf-fields))
                              #{field-attribute}
                              (when-let [secondary-attribute (->> field-attribute
                                                                  (registry/get-field-schema
                                                                   tenant-schema)
                                                                  :field/secondary-attribute)]
                                #{secondary-attribute}))))
          ;; We MUST Deref the atom to propagate changes to the root node - reagent documentation is lacking
          ;; - cursors are LAZY and will not cause rerender on parent cursor/atom unless dereffed -> sync writes up the chain
          (when zadmin-state
            (:tenant/blacklisted-fields @zadmin-state))))}
     [:option
      {:value "default"} "Default"]
     [:option
      {:value "whitelist"} "Whitelist"]
     [:option
      {:value "blacklist"} "Blacklist"]]))

(defn not-in-use-cell
  []
  [:i.fal.fa-ban
   {:class "px-2 flex justify-center w-6"}])

(defn render-data
  [{:keys [entity-type settings-state zadmin-state row-data pipeline-id]}]
  (let [field-attribute (:field-settings/attribute row-data)
        tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema field-attribute)
        zadmin-allowed-field-configuration
        (and (not= :blacklist (:field-settings/zadmin-status row-data))
             (or (= :whitelist (:field-settings/zadmin-status row-data))
                 (not= :not-in-use-blocked
                       (->> field-schema
                            :field/default-visibility
                            :field-visibility))))
        in-use-kw (:field-settings/in-use-config row-data)
        result
        (->>
         row-data
         (map
          (fn [[column-attribute value]]
            [column-attribute
             (case column-attribute
               :field-settings/attribute (str value)
               :field-settings/field-name (str value)
               :field-settings/field-type (str value)
               :field-settings/field-options
               [field-options-setting
                {:field-schema    field-schema
                 :field-attribute field-attribute
                 :settings-state  settings-state
                 :pipeline-id     pipeline-id
                 :entity-type     entity-type}]
               :field-settings/zadmin-status
               (if (= :in-use-disabled (:field-settings/in-use-config row-data))
                 [not-in-use-cell]
                 (r/as-element
                  [zadmin-setting field-attribute value zadmin-state]))

               :field-settings/selected-user-setting (r/as-element
                                                      [selected-user-setting field-attribute value])
               :field-settings/selected-user-setting-old (r/as-element
                                                          [selected-user-setting field-attribute
                                                           value])
               (if-not zadmin-allowed-field-configuration
                 [not-in-use-cell]
                 (if (= column-attribute :field-settings/in-use-config)
                   (r/as-element
                    [in-use-setting
                     {:entity-type      entity-type
                      :column-attribute column-attribute
                      :field-attribute  field-attribute
                      :value            value
                      :settings-state   settings-state
                      :disabled?        (= :in-use-disabled in-use-kw)}])
                   (if-not (some #{in-use-kw} field-permissions/POSITIVE-IN-USE-VALUES)
                     "-"
                     (r/as-element
                      [team-setting
                       {:entity-type      entity-type
                        :column-attribute column-attribute
                        :field-attribute  field-attribute
                        :value            value
                        :settings-state   settings-state
                        :disabled?        (or (= :in-use-disabled in-use-kw)
                                              (:field/calculated-field field-schema))}])))))]))
         (into {}))]
    result))

(defn column
  [pipeline-id all-teams args]
  (let [{:keys [label attribute field-schema filter-data sorting-disabled? width hover-atom
                recompute-grid-sizes! flex-grow selected-entity-type show-zadmin-columns?]}
        args
        default-width (-> (widths/get-column-width attribute)
                          :default-width
                          (or widths/default-width))]
    {:label               label
     :data-key            attribute
     :field-schema        field-schema
     :filter-data         filter-data
     :sort-disabled?      (boolean sorting-disabled?)
     :min-width           (-> (widths/get-column-width attribute)
                              :min-width
                              (or widths/min-width))
     :max-width           (-> (widths/get-column-width attribute)
                              :max-width
                              (or widths/max-width))
     :default-width       default-width
     :width               (or width default-width)
     :set-width           width
     :begin-width-update! (partial grid/begin-width-update! hover-atom attribute)
     :update-width!       (partial grid/update-width!
                                   (events/setup-args {:pipeline-id          pipeline-id
                                                       :all-teams            all-teams
                                                       :selected-entity-type selected-entity-type
                                                       :show-zadmin-columns? show-zadmin-columns?})
                                   recompute-grid-sizes!
                                   hover-atom
                                   attribute)
     :end-width-update!   (partial grid/end-width-update! hover-atom attribute)
     :get-styling         (partial grid/get-styling hover-atom attribute)
     :flex-grow           flex-grow}))

(defn render-user-selector
  [grid-store calc! recalc! {:keys [cell-key style] :as _args}]
  [:div
   {:class "px-2 flex items-center bg-base-light border border-solid border-base-light-border-paler cursor-move even"
    :key   cell-key
    :style (merge style
                  {:will-change "transform"})}
   [:select
    {:class     "w-44 m-0 p-1 text-sm text-base-light-text border-base-light-border-paler bg-base-light disabled:bg-disabled-light disabled:text-disabled-light-text"
     :on-change (fn [e]
                  (let [account-id (->> e
                                        .-target
                                        .-value
                                        (js/parseInt))]
                    (if (= account-id -1)
                      (swap! grid-store dissoc :selected-user)
                      (swap! grid-store assoc :selected-user account-id))
                    (deref grid-store)
                    (calc!)
                    (recalc!)))
     :value     (or (:selected-user @grid-store) -1)}
    [:option
     {:value -1}
     (loc/tr :test-user-access)]
    (->> @session/all-accounts
         (keep (fn [a]
                 (when-not (= (:account/first-name a) "system")
                   [:option
                    {:key   (str cell-key "-option-" (:db/id a))
                     :value (:db/id a)}
                    (entity/display-string a)])))
         (doall))]])

(defn create-props-atom
  [all-teams
   pipeline-id
   field-settings-path
   table-state
   selected
   filters
   sorted-fields
   grid-store
   recompute-grid-sizes!
   hover-atom
   settings-state
   zadmin-state
   show-zadmin-columns?]
  (let [tenant-schema @session/tenant-schema
        active-teams (->> @session/all-teams
                          (remove :common/disabled?))
        recalc! #(grid/recalculate (events/setup-args {:pipeline-id          pipeline-id
                                                       :all-teams            all-teams
                                                       :selected-entity-type (some->> table-state
                                                                                      (deref)
                                                                                      :entity-type)
                                                       :show-zadmin-columns? show-zadmin-columns?
                                                       }))
        calc! (fn []
                (let [entity-type (:entity-type @table-state)]
                  (events/calculate-grid-entities {:entity-type          entity-type
                                                   :grid-store           grid-store
                                                   :pipeline-id          pipeline-id
                                                   :field-settings-path  field-settings-path
                                                   :active-teams         active-teams
                                                   :settings-state       settings-state
                                                   :zadmin-state         zadmin-state
                                                   :show-zadmin-columns? show-zadmin-columns?})))
        columns (->> (events/column-keys pipeline-id
                                         (:entity-type @table-state)
                                         all-teams
                                         (and show-zadmin-columns? (session/zadmin?)))
                     (map (fn [k]
                            (let [field-schema (registry/get-field-schema tenant-schema k)
                                  team-id (field-permissions/parse-team-id k)
                                  label (if-not team-id
                                          (attribute/attribute-label tenant-schema k)
                                          (->> (get @session/entities team-id)
                                               (:team/name)))]
                              (column
                               pipeline-id
                               all-teams
                               {:label                 label
                                :attribute             k
                                :field-schema          field-schema
                                :filter-data           (get filters k)
                                :sorting-disabled?     false
                                :width                 (grid/get-saved-width
                                                        (events/setup-args
                                                         {:pipeline-id          pipeline-id
                                                          :all-teams            all-teams
                                                          :selected-entity-type (some->> table-state
                                                                                         (deref)
                                                                                         :entity-type)
                                                          :show-zadmin-columns? show-zadmin-columns?})
                                                        k)
                                :hover-atom            hover-atom
                                :recompute-grid-sizes! recompute-grid-sizes!
                                :flex-grow             1
                                :show-zadmin-columns?  show-zadmin-columns?}))))
                     (vec))]
    (r/atom
     {:grid-store              grid-store
      :setup-args              (events/setup-args {:pipeline-id          pipeline-id
                                                   :all-teams            all-teams
                                                   :selected-entity-type (some->> table-state
                                                                                  (deref)
                                                                                  :entity-type)
                                                   :show-zadmin-columns? show-zadmin-columns?})
      :selected                selected
      :custom-header-renderer  (fn [data-key]
                                 (when (some #{data-key}
                                             [:field-settings/selected-user-setting
                                              :field-settings/selected-user-setting-old])
                                   (fn [{:keys [cell-key style data-key] :as args}]
                                     (if (= data-key :field-settings/selected-user-setting)
                                       (render-user-selector grid-store calc! recalc! args)
                                       [:div
                                        {:key   cell-key
                                         :style (merge style
                                                       {:will-change "transform"})}
                                        (some->> @grid-store
                                                 :selected-user
                                                 (get @session/entities)
                                                 (entity/display-string))]))))
      :custom-tools
      (fn [data-key]
        (when-not
         (some #{data-key}
               [:field-settings/attribute
                :field-settings/zadmin-status
                :field-settings/field-name
                :field-settings/field-type
                :field-settings/field-options
                :field-settings/in-use-config
                :field-settings/multiplier
                :field-settings/constraints])
          [{:tool-icon :fa-copy
            :tool-id   :permissions-copy-tool
            :on-click! (fn [data-key]
                         (modals/open-modal!
                          copy-permissions/modal-key
                          {:default-source data-key
                           :options        (->> columns
                                                (filter (fn [c]
                                                          (events/copy-target? (:data-key c))))
                                                (map (fn [c]
                                                       {:value (:data-key c)
                                                        :label (:label c)}))
                                                (into []))
                           :on-submit!     (fn [copy-from copy-to on-close!]
                                             (let [tab-entity-type (some->> table-state
                                                                            (deref)
                                                                            :entity-type)
                                                   grid-entities (:grid-entities @db/fields)]
                                               (time (->> grid-entities
                                                          (map (fn [row-data]
                                                                 (swap! settings-state assoc-in
                                                                        [tab-entity-type
                                                                         (:field-settings/attribute
                                                                          row-data) copy-to]
                                                                        (get row-data copy-from))
                                                                 #_(deref settings-state)))
                                                          (doall)))
                                               ;; force cursor sync up to the atom
                                               (deref settings-state)
                                               (on-close!)))}))}]))
      :header-height           50
      :select-width            0
      :min-columns-width       30
      :height                  (- (or (:height @app-db/window) 400)
                                  75)                                                               ;; TODO: lazy - fixed 120px for button row in this view
      :avail-height            (- (or (:height @app-db/window) 400)
                                  75)                                                               ;; TODO: lazy - fixed 120px for button row in this view
      :useDynamicRowHeight     false                                                                ;; TODO: kebab-case
      :row-height              50                                                                   ;(fn [index] 40)
      :row-count               (if (sequential? sorted-fields) (count sorted-fields) 0)
      :scrollToIndex           0
      :columns                 columns
      :overscan-count          1
      :no-rows-renderer        (fn [& _args]
                                 (r/as-element
                                  [:div
                                   {:class "my-6 text-center"}
                                   "No Rows"]))
      :is-sorted-by?           (partial grid/is-sorted-by db/view-settings)
      :is-secondary-sorted-by? (partial grid/is-secondary-sorted-by db/view-settings)
      :sort-by!                (fn [k dir]
                                 (grid/set-sort-by (events/setup-args
                                                    {:pipeline-id          pipeline-id
                                                     :all-teams            all-teams
                                                     :selected-entity-type (some->> table-state
                                                                                    (deref)
                                                                                    :entity-type)
                                                     :show-zadmin-columns? show-zadmin-columns?
                                                     })
                                                   k
                                                   dir))
      :reorder-columns!        (fn [dragged-column after-column]
                                 (grid/reorder-columns! (events/setup-args
                                                         {:pipeline-id          pipeline-id
                                                          :all-teams            all-teams
                                                          :selected-entity-type (some->>
                                                                                 table-state
                                                                                 (deref)
                                                                                 :entity-type)
                                                          :show-zadmin-columns?
                                                          show-zadmin-columns?})
                                                        dragged-column
                                                        after-column)
                                 (recompute-grid-sizes!))
      :can-filter?             (fn [_k]
                                 false)
      :resizing-column         (some-> @hover-atom
                                       (get :resize-start-width)
                                       (first)
                                       (key))
      :recompute-grid-sizes!   recompute-grid-sizes!
      :row-data-getter         (fn [index]
                                 (let [data (when (< index (count sorted-fields))
                                              (nth sorted-fields index))]
                                   (render-data
                                    {:entity-type    (:entity-type @table-state)
                                     :settings-state settings-state
                                     :zadmin-state   zadmin-state
                                     :row-data       data
                                     :pipeline-id    pipeline-id})))})))


;; state
;; {<attrib> {:field-settings/in-use? boolean
;;               :field-settings/buy-side-guest [:r :rw :m :none]
;;               :field-settings/sell-side-guest [:r :rw :m :none]
;;               :field-settings.team/id-<team-id> [:r :rw :m :none]

(defn render
  [grid-store
   {:keys [pipeline-id field-settings-path table-state settings-state zadmin-state
           show-zadmin-columns?]
    :as   _view-args} _grid-args]
  (let [hover-atom (r/atom {:hover {:col nil :row nil}})
        recompute-grid-sizes! (fn []
                                (js/setTimeout
                                 (partial vu/recompute-grid-sizes-with-state! grid-store)
                                 10))]
    (fn [_grid-args]
      (let [filters {}
            #_@db/company-filters                                                                   ;;TODO later
            sorted-fields (:sorted-grid-entities @db/fields)
            active-teams (->> @session/all-teams
                              (remove :common/disabled?))
            grid-atom (create-props-atom active-teams
                                         pipeline-id
                                         field-settings-path
                                         table-state
                                         nil
                                         filters
                                         sorted-fields
                                         grid-store
                                         recompute-grid-sizes!
                                         hover-atom
                                         settings-state
                                         zadmin-state
                                         show-zadmin-columns?)]
        [scrollable-grid/view
         {:props-atom grid-atom
          :hover-atom hover-atom}]))))


(defn view
  [{:keys [pipeline-id local-storage-path table-state zadmin-state settings-state
           show-zadmin-columns?]
    :as   _args}]
  (let [grid-store (atom {})
        resizer (fn []
                  (js/setTimeout (partial vu/recompute-grid-sizes-with-state! grid-store) 100))
        setup! (fn []
                 (events/setup {:pipeline-id          pipeline-id
                                :field-settings-path  local-storage-path
                                :grid-store           grid-store
                                :table-state          table-state
                                :settings-state       settings-state
                                :zadmin-state         zadmin-state
                                :show-zadmin-columns? show-zadmin-columns?}))]
    (.addEventListener js/window "resize" resizer)
    (r/create-class
     {:component-did-mount    (fn [& _args]
                                (setup!))
      :component-will-unmount (fn []
                                (events/cleanup table-state settings-state zadmin-state)
                                (.removeEventListener js/window "resize" resizer))
      :reagent-render         (partial render
                                       grid-store
                                       {:field-settings-path  local-storage-path
                                        :pipeline-id          pipeline-id
                                        :zadmin-state         zadmin-state
                                        :table-state          table-state
                                        :settings-state       settings-state
                                        :show-zadmin-columns? show-zadmin-columns?})})))
